import React, { useState, useEffect } from 'react';
import { 
    Package, 
    Search, 
    Plus, 
    Edit3, 
    Trash2, 
    X, 
    Save, 
    AlertTriangle, 
    XCircle, 
    CheckCircle2, 
    Loader2
} from 'lucide-react';
import { 
    collection, 
    doc, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    limit, 
    startAfter,
    getDocs
} from 'firebase/firestore';
import { execute, field, stringContains, toLower } from 'firebase/firestore/pipelines';
import { db, auth } from '../../utils/firebase';
import { toast } from 'sonner';

const INVENTORY_LIMIT = 50;

export default function Inventory() {
    const [userId, setUserId] = useState(null);

    // User Inventory List State with Pagination (from users/{uid}/inventory)
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loadingInventory, setLoadingInventory] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);

    // Search & Filter States
    const [searchQuery, setSearchQuery] = useState('');
    const [stockFilter, setStockFilter] = useState('all'); // all, in_stock, low_stock, out_of_stock
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [categoriesList, setCategoriesList] = useState([]);

    // Add Product to Inventory Modal State (Batch Entry)
    const [showAddModal, setShowAddModal] = useState(false);
    const [productSearchQuery, setProductSearchQuery] = useState('');
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [isSearchingProducts, setIsSearchingProducts] = useState(false);
    
    // Batch Queue of products being added
    const [selectedProductsQueue, setSelectedProductsQueue] = useState([]);
    const [isAddingItemsBatch, setIsAddingItemsBatch] = useState(false);

    // Edit Inventory Item Modal State
    const [editingItem, setEditingItem] = useState(null);
    const [editFormData, setEditFormData] = useState({
        productName: '',
        productCode: '',
        stock: '',
        categoryName: '',
        notes: ''
    });
    const [isSavingEdit, setIsSavingEdit] = useState(false);

    // Inline Stock Editing State
    const [stockInputs, setStockInputs] = useState({});
    const [savingStockIds, setSavingStockIds] = useState({});

    // Auth & Inventory Fetch Handler with 50-item Query Limit
    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserId(user.uid);
                fetchInventory(user.uid, true);
            } else {
                setUserId(null);
                setInventoryItems([]);
                setLoadingInventory(false);
                setHasMore(false);
            }
        });

        return () => unsubscribeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch Inventory with limit(50) and startAfter(lastVisible)
    const fetchInventory = async (uid, isInitial = false) => {
        if (!uid) return;
        if (isInitial) {
            setLoadingInventory(true);
            setLastVisible(null);
        } else {
            setLoadingMore(true);
        }

        try {
            const inventoryRef = collection(db, "users", uid, "inventory");
            let q;

            if (isInitial) {
                q = query(inventoryRef, limit(INVENTORY_LIMIT));
            } else if (lastVisible) {
                q = query(inventoryRef, startAfter(lastVisible), limit(INVENTORY_LIMIT));
            } else {
                setLoadingInventory(false);
                setLoadingMore(false);
                return;
            }

            const snapshot = await getDocs(q);
            const list = snapshot.docs.map(docSnap => {
                const data = docSnap.data();
                return {
                    id: docSnap.id,
                    productId: data.productId || '',
                    productName: data.productName || data.title || 'Untitled Product',
                    productCode: data.productCode || data.code || '',
                    stock: data.stock !== undefined && data.stock !== null ? Number(data.stock) : 0,
                    categoryName: data.categoryName || data.category || 'General',
                    subCategoryName: data.subCategoryName || data.subcategory || '',
                    image: data.image || '/product-placeholder.png',
                    notes: data.notes || '',
                    raw: data
                };
            });

            if (isInitial) {
                setInventoryItems(list);
            } else {
                setInventoryItems(prev => [...prev, ...list]);
            }

            // Extract categories
            const allItems = isInitial ? list : [...inventoryItems, ...list];
            const cats = Array.from(new Set(allItems.map(p => p.categoryName).filter(Boolean)));
            setCategoriesList(cats);

            const lastDoc = snapshot.docs[snapshot.docs.length - 1];
            setLastVisible(lastDoc || null);
            setHasMore(snapshot.docs.length === INVENTORY_LIMIT);
        } catch (error) {
            console.error("Error fetching inventory items:", error);
            toast.error("Failed to load inventory");
        } finally {
            setLoadingInventory(false);
            setLoadingMore(false);
        }
    };

    // Live Search Products from global 'products' collection
    useEffect(() => {
        const queryStr = productSearchQuery.trim().toLowerCase();
        if (!queryStr) {
            setProductSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setIsSearchingProducts(true);
            try {
                // Pipeline query on products collection
                const snapshot = await execute(
                    db.pipeline()
                        .collection('products')
                        .where(stringContains(toLower(field('productName')), queryStr))
                        .limit(8)
                );
                const results = snapshot.results.map(r => {
                    const data = r.data();
                    const docId = r.id || '';
                    return {
                        id: docId,
                        productName: data.productName || data.title || '',
                        productCode: data.productCodeLetters && data.productCode 
                            ? `${data.productCodeLetters}${data.productCode}` 
                            : (data.code || ''),
                        image: data.image || (data.images?.[0]) || '/product-placeholder.png',
                        categoryName: data.categoryName || data.category || 'General',
                        subCategoryName: data.subCategoryName || data.subcategory || '',
                        raw: data
                    };
                });
                setProductSearchResults(results);
            } catch (e) {
                // Fallback getDocs search
                try {
                    const snap = await getDocs(query(collection(db, 'products'), limit(30)));
                    const filtered = snap.docs
                        .map(d => {
                            const data = d.data();
                            return {
                                id: d.id,
                                productName: data.productName || data.title || '',
                                productCode: data.productCodeLetters && data.productCode 
                                    ? `${data.productCodeLetters}${data.productCode}` 
                                    : (data.code || ''),
                                image: data.image || (data.images?.[0]) || '/product-placeholder.png',
                                categoryName: data.categoryName || data.category || 'General',
                                subCategoryName: data.subCategoryName || data.subcategory || '',
                                raw: data
                            };
                        })
                        .filter(p => p.productName.toLowerCase().includes(queryStr) || p.productCode.toLowerCase().includes(queryStr));
                    setProductSearchResults(filtered.slice(0, 8));
                } catch (fallbackErr) {
                    console.error("Error searching products collection:", fallbackErr);
                }
            } finally {
                setIsSearchingProducts(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [productSearchQuery]);

    // Queue product into batch selection
    const handleQueueProduct = (prod) => {
        setSelectedProductsQueue(prev => {
            const idx = prev.findIndex(item => item.id === prod.id);
            if (idx > -1) {
                const updated = [...prev];
                updated[idx].stock = Number(updated[idx].stock || 0) + 1;
                return updated;
            } else {
                return [...prev, {
                    ...prod,
                    stock: 10,
                    notes: ''
                }];
            }
        });
        toast.success(`Queued "${prod.productName}"`);
    };

    // Remove queued product from batch selection
    const handleRemoveQueuedProduct = (productId) => {
        setSelectedProductsQueue(prev => prev.filter(item => item.id !== productId));
    };

    // Update queued item field (stock or notes)
    const handleUpdateQueuedItem = (productId, fieldKey, val) => {
        setSelectedProductsQueue(prev => prev.map(item => {
            if (item.id === productId) {
                return { ...item, [fieldKey]: val };
            }
            return item;
        }));
    };

    // Save Batch Products to User Inventory
    const handleAddBatchProductsToInventory = async (e) => {
        e.preventDefault();
        if (!userId) {
            toast.error("Please sign in to manage inventory");
            return;
        }
        if (selectedProductsQueue.length === 0) {
            toast.error("Please select at least one product from the catalog");
            return;
        }

        setIsAddingItemsBatch(true);
        try {
            const inventoryRef = collection(db, "users", userId, "inventory");

            const promises = selectedProductsQueue.map(async (selectedProd) => {
                const existingIdx = inventoryItems.findIndex(i => i.productId === selectedProd.id || i.productName === selectedProd.productName);

                if (existingIdx > -1) {
                    const existingItem = inventoryItems[existingIdx];
                    const updatedStock = existingItem.stock + Math.max(0, parseInt(selectedProd.stock) || 0);
                    const itemRef = doc(db, "users", userId, "inventory", existingItem.id);
                    return updateDoc(itemRef, {
                        stock: updatedStock,
                        notes: selectedProd.notes ? selectedProd.notes.trim() : existingItem.notes,
                        updatedAt: new Date().toISOString()
                    });
                } else {
                    return addDoc(inventoryRef, {
                        productId: selectedProd.id,
                        productName: selectedProd.productName,
                        productCode: selectedProd.productCode,
                        categoryName: selectedProd.categoryName,
                        subCategoryName: selectedProd.subCategoryName,
                        image: selectedProd.image,
                        stock: Math.max(0, parseInt(selectedProd.stock) || 0),
                        notes: selectedProd.notes ? selectedProd.notes.trim() : '',
                        createdAt: new Date().toISOString()
                    });
                }
            });

            await Promise.all(promises);

            const count = selectedProductsQueue.length;
            toast.success(`Successfully added ${count} ${count === 1 ? 'product' : 'products'} to your inventory!`);

            setShowAddModal(false);
            setSelectedProductsQueue([]);
            setProductSearchQuery('');
            setProductSearchResults([]);

            // Re-fetch initial 50 inventory items to display updated list
            fetchInventory(userId, true);
        } catch (err) {
            console.error("Error adding batch items to user inventory:", err);
            toast.error("Failed to add products to inventory");
        } finally {
            setIsAddingItemsBatch(false);
        }
    };

    // Quick Inline Stock Edit (+ / - / input change)
    const handleStockInputChange = (id, val) => {
        setStockInputs(prev => ({
            ...prev,
            [id]: val
        }));
    };

    const handleSaveStockInline = async (item, newStockVal) => {
        if (!userId) return;
        const stockNum = Math.max(0, parseInt(newStockVal) || 0);
        setSavingStockIds(prev => ({ ...prev, [item.id]: true }));

        try {
            const itemRef = doc(db, "users", userId, "inventory", item.id);
            await updateDoc(itemRef, {
                stock: stockNum,
                updatedAt: new Date().toISOString()
            });

            toast.success(`Updated stock for "${item.productName}" to ${stockNum}`);
            setInventoryItems(prev => prev.map(i => i.id === item.id ? { ...i, stock: stockNum } : i));
            setStockInputs(prev => {
                const copy = { ...prev };
                delete copy[item.id];
                return copy;
            });
        } catch (err) {
            console.error("Error updating inventory stock:", err);
            toast.error("Failed to update stock");
        } finally {
            setSavingStockIds(prev => ({ ...prev, [item.id]: false }));
        }
    };

    // Edit Inventory Item Modal
    const handleOpenEditModal = (item) => {
        setEditingItem(item);
        setEditFormData({
            productName: item.productName,
            productCode: item.productCode,
            stock: item.stock,
            categoryName: item.categoryName,
            notes: item.notes
        });
    };

    const handleSaveEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingItem || !userId) return;

        setIsSavingEdit(true);
        try {
            const itemRef = doc(db, "users", userId, "inventory", editingItem.id);
            await updateDoc(itemRef, {
                productName: editFormData.productName.trim(),
                productCode: editFormData.productCode.trim(),
                stock: Math.max(0, parseInt(editFormData.stock) || 0),
                categoryName: editFormData.categoryName.trim(),
                notes: editFormData.notes.trim(),
                updatedAt: new Date().toISOString()
            });

            toast.success(`Updated inventory item "${editFormData.productName}"`);
            setInventoryItems(prev => prev.map(i => i.id === editingItem.id ? {
                ...i,
                productName: editFormData.productName.trim(),
                productCode: editFormData.productCode.trim(),
                stock: Math.max(0, parseInt(editFormData.stock) || 0),
                categoryName: editFormData.categoryName.trim(),
                notes: editFormData.notes.trim()
            } : i));
            setEditingItem(null);
        } catch (err) {
            console.error("Error updating inventory item:", err);
            toast.error("Failed to update inventory item");
        } finally {
            setIsSavingEdit(false);
        }
    };

    // Delete Inventory Item from users/{uid}/inventory
    const handleDeleteItem = async (item) => {
        if (!userId) return;
        if (!window.confirm(`Remove "${item.productName}" from your inventory?`)) return;

        try {
            const itemRef = doc(db, "users", userId, "inventory", item.id);
            await deleteDoc(itemRef);
            toast.success(`Removed "${item.productName}" from inventory`);
            setInventoryItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
            console.error("Error removing inventory item:", err);
            toast.error("Failed to remove item");
        }
    };

    // Filter Inventory Items
    const filteredItems = inventoryItems.filter(item => {
        const queryLower = searchQuery.trim().toLowerCase();
        if (queryLower) {
            const matchesName = item.productName.toLowerCase().includes(queryLower);
            const matchesCode = item.productCode.toLowerCase().includes(queryLower);
            const matchesCat = item.categoryName.toLowerCase().includes(queryLower);
            if (!matchesName && !matchesCode && !matchesCat) return false;
        }

        if (stockFilter === 'in_stock' && item.stock <= 5) return false;
        if (stockFilter === 'low_stock' && (item.stock <= 0 || item.stock > 5)) return false;
        if (stockFilter === 'out_of_stock' && item.stock > 0) return false;

        if (categoryFilter !== 'all' && item.categoryName !== categoryFilter) return false;

        return true;
    });

    // Metric Counts
    const totalCount = inventoryItems.length;
    const inStockCount = inventoryItems.filter(i => i.stock > 5).length;
    const lowStockCount = inventoryItems.filter(i => i.stock > 0 && i.stock <= 5).length;
    const outOfStockCount = inventoryItems.filter(i => i.stock <= 0).length;

    return (
        <div className="min-h-screen bg-[#FAF9F6] pb-24 pt-8">
            <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">

                {/* ================= HEADER ================= */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 bg-white p-6 sm:p-8 rounded-3xl border border-[#E6E6E6] shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-[#154734] text-white rounded-2xl flex items-center justify-center shadow-lg shadow-[#154734]/20 shrink-0">
                            <Package className="w-7 h-7" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-black text-[#154734] tracking-tight">My Kitchen Inventory</h1>
                            <p className="text-sm text-slate-500 font-semibold mt-1">
                                Track, manage and restock your restaurant's inventory items
                            </p>
                        </div>
                    </div>

                    {userId && (
                        <button
                            onClick={() => {
                                setShowAddModal(true);
                                setSelectedProductsQueue([]);
                                setProductSearchQuery('');
                                setProductSearchResults([]);
                            }}
                            className="flex items-center justify-center gap-2 bg-[#FF7A00] hover:bg-[#E66E00] text-white font-extrabold px-6 py-3.5 rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer border-none text-sm shrink-0"
                        >
                            <Plus className="w-5 h-5 stroke-[3]" />
                            <span>Add Products to Inventory</span>
                        </button>
                    )}
                </div>

                {!userId ? (
                    <div className="bg-white rounded-3xl border border-[#E6E6E6] p-12 text-center max-w-lg mx-auto shadow-sm my-12">
                        <div className="w-16 h-16 bg-[#FFF7EC] text-[#154734] rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Package className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-black text-[#154734]">Please Sign In</h3>
                        <p className="text-slate-500 text-sm font-medium mt-2 mb-6">
                            Sign in to manage your kitchen stock and store your custom inventory list.
                        </p>
                        <button
                            onClick={() => window.dispatchEvent(new Event('open-auth'))}
                            className="bg-[#154734] text-white font-extrabold px-6 py-3 rounded-2xl hover:bg-[#0A2E22] transition-all cursor-pointer border-none text-sm shadow-md"
                        >
                            Sign In / Register
                        </button>
                    </div>
                ) : (
                    <>
                        {/* ================= METRICS DASHBOARD ================= */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            <div 
                                onClick={() => setStockFilter('all')}
                                className={`bg-white p-5 rounded-3xl border transition-all cursor-pointer ${
                                    stockFilter === 'all' ? 'border-[#154734] ring-2 ring-[#154734]/20 shadow-md' : 'border-[#E6E6E6] hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between text-slate-400 mb-2">
                                    <span className="text-xs font-black uppercase tracking-wider">Total Loaded</span>
                                    <Package className="w-5 h-5 text-[#154734]" />
                                </div>
                                <div className="text-2xl sm:text-3xl font-black text-[#154734]">{totalCount}</div>
                            </div>

                            <div 
                                onClick={() => setStockFilter('in_stock')}
                                className={`bg-white p-5 rounded-3xl border transition-all cursor-pointer ${
                                    stockFilter === 'in_stock' ? 'border-emerald-600 ring-2 ring-emerald-600/20 shadow-md' : 'border-[#E6E6E6] hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between text-emerald-600 mb-2">
                                    <span className="text-xs font-black uppercase tracking-wider">In Stock</span>
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                </div>
                                <div className="text-2xl sm:text-3xl font-black text-emerald-700">{inStockCount}</div>
                            </div>

                            <div 
                                onClick={() => setStockFilter('low_stock')}
                                className={`bg-white p-5 rounded-3xl border transition-all cursor-pointer ${
                                    stockFilter === 'low_stock' ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-md' : 'border-[#E6E6E6] hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between text-amber-500 mb-2">
                                    <span className="text-xs font-black uppercase tracking-wider">Low Stock (≤5)</span>
                                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                                </div>
                                <div className="text-2xl sm:text-3xl font-black text-amber-600">{lowStockCount}</div>
                            </div>

                            <div 
                                onClick={() => setStockFilter('out_of_stock')}
                                className={`bg-white p-5 rounded-3xl border transition-all cursor-pointer ${
                                    stockFilter === 'out_of_stock' ? 'border-red-500 ring-2 ring-red-500/20 shadow-md' : 'border-[#E6E6E6] hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center justify-between text-red-500 mb-2">
                                    <span className="text-xs font-black uppercase tracking-wider">Out of Stock</span>
                                    <XCircle className="w-5 h-5 text-red-500" />
                                </div>
                                <div className="text-2xl sm:text-3xl font-black text-red-600">{outOfStockCount}</div>
                            </div>
                        </div>

                        {/* ================= SEARCH & FILTERS BAR ================= */}
                        <div className="bg-white p-4 sm:p-6 rounded-3xl border border-[#E6E6E6] shadow-sm mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
                            {/* Search */}
                            <div className="relative w-full md:w-96">
                                <Search className="w-4 h-4 text-slate-400 absolute left-4 top-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search your inventory items..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl pl-11 pr-10 py-3 text-xs sm:text-sm font-semibold text-slate-800 outline-none focus:border-[#154734] focus:ring-2 focus:ring-[#154734]/20 transition-all"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 bg-transparent border-none cursor-pointer"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Filters */}
                            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-500">Stock Status:</span>
                                    <select
                                        value={stockFilter}
                                        onChange={(e) => setStockFilter(e.target.value)}
                                        className="bg-[#FAF9F6] border border-[#E6E6E6] text-slate-800 text-xs font-extrabold rounded-2xl px-3 py-2.5 outline-none cursor-pointer focus:border-[#154734]"
                                    >
                                        <option value="all">All Items</option>
                                        <option value="in_stock">In Stock (&gt;5)</option>
                                        <option value="low_stock">Low Stock (1–5)</option>
                                        <option value="out_of_stock">Out of Stock (0)</option>
                                    </select>
                                </div>

                                {categoriesList.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-slate-500">Category:</span>
                                        <select
                                            value={categoryFilter}
                                            onChange={(e) => setCategoryFilter(e.target.value)}
                                            className="bg-[#FAF9F6] border border-[#E6E6E6] text-slate-800 text-xs font-extrabold rounded-2xl px-3 py-2.5 outline-none cursor-pointer focus:border-[#154734] max-w-[180px] truncate"
                                        >
                                            <option value="all">All Categories</option>
                                            {categoriesList.map((cat, idx) => (
                                                <option key={idx} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {(searchQuery || stockFilter !== 'all' || categoryFilter !== 'all') && (
                                    <button
                                        onClick={() => {
                                            setSearchQuery('');
                                            setStockFilter('all');
                                            setCategoryFilter('all');
                                        }}
                                        className="text-xs font-bold text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer px-2 py-1"
                                    >
                                        Reset Filters
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ================= INVENTORY TABLE ================= */}
                        <div className="bg-white rounded-3xl border border-[#E6E6E6] shadow-sm overflow-hidden">
                            {loadingInventory ? (
                                <div className="flex flex-col items-center justify-center py-24">
                                    <Loader2 className="w-10 h-10 text-[#154734] animate-spin mb-3" />
                                    <p className="text-slate-500 font-bold text-sm">Loading your inventory...</p>
                                </div>
                            ) : filteredItems.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <h3 className="text-lg font-bold text-slate-700">No inventory items found</h3>
                                    <p className="text-slate-500 text-xs font-semibold mt-1 mb-6">
                                        Click "+ Add Products to Inventory" above to search catalog items and add multiple products to your kitchen stock!
                                    </p>
                                    <button
                                        onClick={() => {
                                            setShowAddModal(true);
                                            setSelectedProductsQueue([]);
                                            setProductSearchQuery('');
                                            setProductSearchResults([]);
                                        }}
                                        className="bg-[#154734] hover:bg-[#0A2E22] text-white font-extrabold px-6 py-3 rounded-2xl transition-all cursor-pointer border-none text-xs shadow-md"
                                    >
                                        + Add Products
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-[#FAF9F6] border-b border-[#E6E6E6] text-[11px] font-black text-slate-500 uppercase tracking-wider">
                                                    <th className="py-4 px-4 w-16 text-center">Sl. No.</th>
                                                    <th className="py-4 px-6">Product Details</th>
                                                    <th className="py-4 px-4">Code</th>
                                                    <th className="py-4 px-4">Category</th>
                                                    <th className="py-4 px-6 min-w-[200px]">Stock Level (Units)</th>
                                                    <th className="py-4 px-4">Status</th>
                                                    <th className="py-4 px-6 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[#F5F5F5] text-xs">
                                                {filteredItems.map((item, idx) => {
                                                    const currentStockInput = stockInputs[item.id] !== undefined ? stockInputs[item.id] : item.stock;
                                                    const isModified = stockInputs[item.id] !== undefined && Number(stockInputs[item.id]) !== item.stock;
                                                    const isSavingThis = savingStockIds[item.id];

                                                    let statusBadge = null;
                                                    if (item.stock <= 0) {
                                                        statusBadge = <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black bg-red-50 text-red-600 border border-red-200"><XCircle className="w-3 h-3" /> Out of Stock</span>;
                                                    } else if (item.stock <= 5) {
                                                        statusBadge = <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black bg-amber-50 text-amber-600 border border-amber-200"><AlertTriangle className="w-3 h-3" /> Low Stock</span>;
                                                    } else {
                                                        statusBadge = <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-600 border border-emerald-200"><CheckCircle2 className="w-3 h-3" /> In Stock</span>;
                                                    }

                                                    return (
                                                        <tr key={item.id} className="hover:bg-[#FAF9F6] transition-colors">
                                                            {/* Sl. No. */}
                                                            <td className="py-4 px-4 text-center font-black text-slate-400">
                                                                {idx + 1}
                                                            </td>

                                                            {/* Product Details */}
                                                            <td className="py-4 px-6">
                                                                <div className="flex items-center gap-3.5">
                                                                    <img
                                                                        src={item.image}
                                                                        alt={item.productName}
                                                                        className="w-11 h-11 object-contain rounded-xl border border-[#E6E6E6] bg-white p-1 shrink-0"
                                                                    />
                                                                    <div className="min-w-0">
                                                                        <div className="font-bold text-slate-800 text-sm truncate max-w-xs">{item.productName}</div>
                                                                        {item.notes && <div className="text-[10px] text-slate-400 font-semibold truncate max-w-xs">{item.notes}</div>}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Code */}
                                                            <td className="py-4 px-4 font-mono font-bold text-slate-600">
                                                                {item.productCode || '-'}
                                                            </td>

                                                            {/* Category */}
                                                            <td className="py-4 px-4">
                                                                <span className="font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg text-[11px]">
                                                                    {item.categoryName}
                                                                </span>
                                                            </td>

                                                            {/* Stock Editor */}
                                                            <td className="py-4 px-6">
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleStockInputChange(item.id, Math.max(0, Number(currentStockInput) - 1))}
                                                                        className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-black flex items-center justify-center border-none cursor-pointer text-sm"
                                                                    >
                                                                        -
                                                                    </button>

                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={currentStockInput}
                                                                        onChange={(e) => handleStockInputChange(item.id, e.target.value)}
                                                                        className={`w-16 text-center font-black text-sm py-1 rounded-xl border outline-none transition-all ${
                                                                            isModified 
                                                                                ? 'bg-amber-50 border-amber-400 text-amber-700 ring-2 ring-amber-400/20' 
                                                                                : 'bg-[#FAF9F6] border-[#E6E6E6] text-[#154734]'
                                                                        }`}
                                                                    />

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleStockInputChange(item.id, Number(currentStockInput) + 1)}
                                                                        className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-black flex items-center justify-center border-none cursor-pointer text-sm"
                                                                    >
                                                                        +
                                                                    </button>

                                                                    {isModified && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSaveStockInline(item, currentStockInput)}
                                                                            disabled={isSavingThis}
                                                                            className="ml-1 px-3 py-1 bg-[#154734] hover:bg-[#0A2E22] text-white text-xs font-bold rounded-xl border-none cursor-pointer transition flex items-center gap-1 shadow-sm"
                                                                            title="Save Stock Level"
                                                                        >
                                                                            {isSavingThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                                                            <span>Save</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Status Badge */}
                                                            <td className="py-4 px-4">
                                                                {statusBadge}
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="py-4 px-6 text-right">
                                                                <div className="flex items-center justify-end gap-2">
                                                                    <button
                                                                        onClick={() => handleOpenEditModal(item)}
                                                                        className="w-8 h-8 rounded-xl bg-slate-100 text-slate-600 hover:bg-[#154734] hover:text-white transition flex items-center justify-center border-none cursor-pointer"
                                                                        title="Edit Inventory Item"
                                                                    >
                                                                        <Edit3 className="w-4 h-4" />
                                                                    </button>

                                                                    <button
                                                                        onClick={() => handleDeleteItem(item)}
                                                                        className="w-8 h-8 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition flex items-center justify-center border-none cursor-pointer"
                                                                        title="Remove Item"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Load More Button (Limit 50 Paging) */}
                                    {hasMore && (
                                        <div className="p-5 bg-[#FAF9F6] border-t border-[#E6E6E6] flex justify-center">
                                            <button
                                                onClick={() => fetchInventory(userId, false)}
                                                disabled={loadingMore}
                                                className="bg-[#154734] hover:bg-[#0A2E22] text-white font-extrabold px-7 py-3 rounded-2xl text-xs sm:text-sm transition-all cursor-pointer border-none shadow-md flex items-center gap-2 hover:-translate-y-0.5"
                                            >
                                                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                                                <span>Load More Inventory Items (50 per batch)</span>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* ================= BATCH ADD PRODUCTS MODAL ================= */}
                {showAddModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border border-[#E6E6E6] max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#FFF7EC] text-[#154734] rounded-xl flex items-center justify-center font-bold">
                                        <Plus className="w-5 h-5 stroke-[3]" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-[#154734]">Add Products to Inventory</h2>
                                        <p className="text-xs text-slate-400 font-semibold m-0">Search & select multiple products from the catalog</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="w-8 h-8 rounded-full bg-[#FAF9F6] text-slate-400 hover:text-slate-700 flex items-center justify-center border-none cursor-pointer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleAddBatchProductsToInventory} className="space-y-6">
                                {/* Search Products in Products Collection */}
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-2">
                                        Search Catalog Products
                                    </label>
                                    <div className="relative">
                                        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-3.5" />
                                        <input
                                            type="text"
                                            placeholder="Type product name or code to search..."
                                            value={productSearchQuery}
                                            onChange={(e) => setProductSearchQuery(e.target.value)}
                                            className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl pl-11 pr-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                        />
                                    </div>

                                    {/* Product Search Results Dropdown */}
                                    {isSearchingProducts ? (
                                        <div className="p-3 text-center text-xs text-slate-400 font-bold">Searching catalog...</div>
                                    ) : productSearchResults.length > 0 ? (
                                        <div className="mt-2 bg-white border border-[#E6E6E6] rounded-2xl max-h-56 overflow-y-auto divide-y divide-[#F5F5F5] shadow-sm">
                                            {productSearchResults.map((prod) => {
                                                const isQueued = selectedProductsQueue.some(item => item.id === prod.id);
                                                return (
                                                    <div 
                                                        key={prod.id} 
                                                        className="p-2.5 flex items-center justify-between hover:bg-[#FAF9F6] transition"
                                                    >
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <img src={prod.image} alt={prod.productName} className="w-8 h-8 object-contain rounded border border-[#E6E6E6] p-0.5 shrink-0" />
                                                            <div className="min-w-0">
                                                                <div className="text-xs font-bold text-slate-800 truncate">{prod.productName}</div>
                                                                <div className="text-[10px] text-slate-400 font-semibold">{prod.categoryName} {prod.productCode && `• ${prod.productCode}`}</div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleQueueProduct(prod)}
                                                            className={`px-3 py-1.5 rounded-xl text-xs font-extrabold border-none cursor-pointer transition shrink-0 ${
                                                                isQueued
                                                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                                                    : 'bg-[#154734] text-white hover:bg-[#0A2E22]'
                                                            }`}
                                                        >
                                                            {isQueued ? '✓ Add More' : '+ Add'}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>

                                {/* Queued Products Batch List */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-xs font-black text-[#154734] uppercase tracking-wider m-0">
                                            Selected Products ({selectedProductsQueue.length})
                                        </h4>
                                        {selectedProductsQueue.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedProductsQueue([])}
                                                className="text-[11px] font-bold text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer"
                                            >
                                                Clear All
                                            </button>
                                        )}
                                    </div>

                                    {selectedProductsQueue.length === 0 ? (
                                        <div className="bg-[#FAF9F6] border border-dashed border-[#D7DDD4] rounded-2xl p-6 text-center text-xs text-slate-400 font-semibold">
                                            No products selected yet. Use the search box above to add multiple products to your batch!
                                        </div>
                                    ) : (
                                        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                                            {selectedProductsQueue.map((item) => (
                                                <div key={item.id} className="bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl p-3.5 space-y-2">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <img src={item.image} alt={item.productName} className="w-9 h-9 object-contain rounded-xl border border-[#E6E6E6] bg-white p-0.5 shrink-0" />
                                                            <div className="min-w-0">
                                                                <div className="text-xs font-bold text-slate-800 truncate">{item.productName}</div>
                                                                <div className="text-[10px] text-slate-400 font-semibold">{item.categoryName} {item.productCode && `(${item.productCode})`}</div>
                                                            </div>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveQueuedProduct(item.id)}
                                                            className="w-7 h-7 rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition flex items-center justify-center border-none cursor-pointer shrink-0"
                                                            title="Remove product"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3 pt-1 border-t border-[#E6E6E6]/60">
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                                                                Stock Quantity
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={item.stock}
                                                                onChange={(e) => handleUpdateQueuedItem(item.id, 'stock', Math.max(0, parseInt(e.target.value) || 0))}
                                                                className="w-full bg-white border border-[#E6E6E6] rounded-xl px-2.5 py-1 text-xs font-black text-[#154734]"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                                                                Notes (Optional)
                                                            </label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. Shelf A1"
                                                                value={item.notes}
                                                                onChange={(e) => handleUpdateQueuedItem(item.id, 'notes', e.target.value)}
                                                                className="w-full bg-white border border-[#E6E6E6] rounded-xl px-2.5 py-1 text-xs font-semibold text-slate-700"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E6E6E6]">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddModal(false)}
                                        className="px-5 py-2.5 rounded-2xl text-xs font-bold text-slate-500 hover:text-slate-800 bg-transparent border-none cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isAddingItemsBatch || selectedProductsQueue.length === 0}
                                        className={`font-extrabold px-6 py-2.5 rounded-2xl text-xs sm:text-sm transition-all border-none shadow-md flex items-center gap-2 ${
                                            selectedProductsQueue.length > 0
                                                ? 'bg-[#154734] hover:bg-[#0A2E22] text-white cursor-pointer'
                                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {isAddingItemsBatch && <Loader2 className="w-4 h-4 animate-spin" />}
                                        <span>
                                            Add All ({selectedProductsQueue.length}) {selectedProductsQueue.length === 1 ? 'Product' : 'Products'} to Inventory
                                        </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= EDIT INVENTORY ITEM MODAL ================= */}
                {editingItem && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-[#E6E6E6] max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#FFF7EC] text-[#154734] rounded-xl flex items-center justify-center font-bold">
                                        <Edit3 className="w-5 h-5" />
                                    </div>
                                    <h2 className="text-xl font-black text-[#154734]">Edit Inventory Item</h2>
                                </div>
                                <button
                                    onClick={() => setEditingItem(null)}
                                    className="w-8 h-8 rounded-full bg-[#FAF9F6] text-slate-400 hover:text-slate-700 flex items-center justify-center border-none cursor-pointer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSaveEditSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-1.5">
                                        Item Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={editFormData.productName}
                                        onChange={(e) => setEditFormData({ ...editFormData, productName: e.target.value })}
                                        required
                                        className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-1.5">
                                            Product Code
                                        </label>
                                        <input
                                            type="text"
                                            value={editFormData.productCode}
                                            onChange={(e) => setEditFormData({ ...editFormData, productCode: e.target.value })}
                                            className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-1.5">
                                            Stock Level (Units) *
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={editFormData.stock}
                                            onChange={(e) => setEditFormData({ ...editFormData, stock: e.target.value })}
                                            required
                                            className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-1.5">
                                        Category
                                    </label>
                                    <input
                                        type="text"
                                        value={editFormData.categoryName}
                                        onChange={(e) => setEditFormData({ ...editFormData, categoryName: e.target.value })}
                                        className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-wider mb-1.5">
                                        Notes / Storage Details
                                    </label>
                                    <input
                                        type="text"
                                        value={editFormData.notes}
                                        onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                                        className="w-full bg-[#FAF9F6] border border-[#E6E6E6] rounded-2xl px-4 py-2.5 text-xs font-semibold text-slate-800 outline-none focus:border-[#154734] transition-all"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E6E6E6]">
                                    <button
                                        type="button"
                                        onClick={() => setEditingItem(null)}
                                        className="px-5 py-2.5 rounded-2xl text-xs font-bold text-slate-500 hover:text-slate-800 bg-transparent border-none cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSavingEdit}
                                        className="bg-[#154734] hover:bg-[#0A2E22] text-white font-extrabold px-6 py-2.5 rounded-2xl text-xs sm:text-sm transition-all border-none cursor-pointer shadow-md flex items-center gap-2"
                                    >
                                        {isSavingEdit && <Loader2 className="w-4 h-4 animate-spin" />}
                                        <span>Save Changes</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
